import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { hasAccessToModule } from "./Pages/rbac/hasAccess";

// Filter menu items by user permissions
export const getVisibleChildren = (item) => {
  if (!item.children) return [];
  return item.children.filter((c) => !c.moduleAccessId || hasAccessToModule(c.moduleAccessId));
};

// Check if the current route is active
export const isItemActive = (item, pathname) => {
  if (item.to && item.to !== "#" && pathname === item.to) return true;
  if (item.children) return item.children.some((c) => isItemActive(c, pathname));
  return false;
};


// Floating nav panel component for L1, L2, L3 dropdown rows
export default function FloatingNav({ items, location, clearNav, headerRef, activeL1, setActiveL1 }) {
  // Track selected menu path across levels
  const [path, setPath] = useState([]);
  const [panelTop, setPanelTop] = useState(64);
  const [headerHeight, setHeaderHeight] = useState(64);
  // Position of dividers and curving brackets under selected buttons
  const [notches, setNotches] = useState({});

  const panelRef = useRef(null);
  const l1Refs = useRef({});
  const rowRefs = useRef({}); // Row container refs
  const btnRefs = useRef({}); // Button element refs

  const isOpen = !!activeL1;
  const l1Item = items.find((i) => i.label === activeL1);

  const closeAll = () => {
    setActiveL1(null);
    setPath([]);
  };

  // Build arrays of menu levels based on user selection
  const buildRows = () => {
    const rows = [];
    let node = l1Item;
    let depth = 0;
    let ancestorKey = "root";
    while (node) {
      const kids = getVisibleChildren(node);
      if (!kids.length) break;
      const activeMatch = kids.find((k) => isItemActive(k, location.pathname));
      const selectedLabel = path[depth] ?? activeMatch?.label;
      rows.push({ depth, kids, selectedLabel, rowKey: ancestorKey });
      if (!selectedLabel) break; // Stop if nothing is selected at this level
      const selected = kids.find((k) => k.label === selectedLabel);
      ancestorKey = `${ancestorKey}>${selected.label}`;
      node = selected;
      depth += 1;
    }
    return rows;
  };

  const rows = l1Item ? buildRows() : [];

  // Measure active button positions to align the decorative brackets
  const measureNotches = () => {
    const next = {};
    rows.forEach((row, rowIdx) => {
      if (rowIdx >= rows.length - 1 || !row.selectedLabel) return; // no row below -> plain flat divider
      const rowEl = rowRefs.current[row.rowKey];
      const btnEl = btnRefs.current[`${row.rowKey}::${row.selectedLabel}`];
      if (!rowEl || !btnEl) return;
      const rowRect = rowEl.getBoundingClientRect();
      const btnRect = btnEl.getBoundingClientRect();
      const width = Math.round(rowRect.width);
      const baseline = Math.round(rowRect.height);
      const sidePad = 4;
      const topPad = 4;
      const top = Math.max(0, Math.round(btnRect.top - rowRect.top) - topPad);
      const left = Math.max(0, Math.round(btnRect.left - rowRect.left) - sidePad);
      const right = Math.min(width, Math.round(btnRect.right - rowRect.left) + sidePad);
      // Ensure corner curves fit the space
      const vSpan = baseline - top;
      const radius = Math.max(
        0,
        Math.min(8, vSpan / 2, (right - left) / 2, left, width - right)
      );
      next[row.rowKey] = { width, height: baseline, top, left, right, radius };
    });
    setNotches(next);
  };

  useLayoutEffect(() => {
    if (!isOpen) return;
    measureNotches();
    window.addEventListener("resize", measureNotches);
    return () => window.removeEventListener("resize", measureNotches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, path, activeL1, location.pathname, panelTop]);

  // Generate SVG path for the bracket framing the active button
  const notchPath = ({ width, height: baseline, top, left, right, radius: r }) =>
    [
      `M 0 ${baseline}`,
      `L ${left - r} ${baseline}`,
      `Q ${left} ${baseline} ${left} ${baseline - r}`,
      `L ${left} ${top + r}`,
      `Q ${left} ${top} ${left + r} ${top}`,
      `L ${right - r} ${top}`,
      `Q ${right} ${top} ${right} ${top + r}`,
      `L ${right} ${baseline - r}`,
      `Q ${right} ${baseline} ${right + r} ${baseline}`,
      `L ${width} ${baseline}`,
    ].join(" ");

  // Position panel right under the main header
  useLayoutEffect(() => {
    const update = () => {
      const rect = headerRef.current?.getBoundingClientRect();
      if (rect) {
        setPanelTop(rect.bottom + 48);
        setHeaderHeight(rect.height);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isOpen, headerRef]);

  const openL1 = (item) => {
    const kids = getVisibleChildren(item);
    if (!kids.length) {
      if (item.to && item.to !== "#") clearNav(item.to);
      return;
    }
    if (activeL1 === item.label) {
      closeAll();
      return;
    }
    setActiveL1(item.label);
    setPath([]);
  };

  // Handle clicking items that have submenus or final links
  const selectAtDepth = (depth, child) => {
    if (getVisibleChildren(child).length > 0) {
      setPath((prev) => {
        const next = prev.slice(0, depth);
        next[depth] = child.label;
        return next;
      });
    } else if (child.to && child.to !== "#") {
      clearNav(child.to);
      closeAll();
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    const onMouseDown = (e) => {
      const insidePanel = panelRef.current?.contains(e.target);
      const insideL1 = Object.values(l1Refs.current).some((el) => el?.contains(e.target));
      if (!insidePanel && !insideL1) closeAll();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    closeAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <>
      {items
        .filter((item) => !item.moduleAccessId || hasAccessToModule(item.moduleAccessId))
        .map((item, idx) => {
          const active = activeL1 === item.label || isItemActive(item, location.pathname);
          return (
            <div
              key={idx}
              ref={(el) => (l1Refs.current[item.label] = el)}
              className="flex-shrink-0 flex items-center self-center"
            >
              <button
                onClick={() => openL1(item)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[14px] font-medium transition-all duration-300 ease-in-out whitespace-nowrap
                  ${active
                    ? "bg-white/15 text-white border border-white/25 shadow-sm"
                    : "text-white/90 hover:bg-white/15 hover:text-white border border-transparent hover:border-white/20"
                  }`}
              >
                {item.label}
              </button>
            </div>
          );
        })}

      {isOpen &&
        createPortal(
          <>
            <div
              className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[998] transition-opacity duration-200"
              onClick={closeAll}
            />
            {/* Dim the main navbar while dropdown is open */}
            <div
              style={{ height: headerHeight }}
              className="fixed inset-x-0 top-0 z-[1001] bg-black/20 pointer-events-none transition-opacity duration-200"
            />
            <div
              ref={panelRef}
              style={{ top: panelTop }}
              className="fixed left-1/2 -translate-x-1/2 z-[999] w-[min(880px,calc(100vw-40px))] animate-floating-panel"
            >
              <div
                className="rounded-[20px] border border-white/[0.18] overflow-hidden"
                style={{
                  backdropFilter: "blur(18px)",
                  WebkitBackdropFilter: "blur(18px)",
                  background: "rgba(82, 136, 239, 0.55)",
                  boxShadow: "0 20px 60px rgba(0,0,0,.35)",
                }}
              >
                {rows.map((row, rowIdx) => {
                  const hasDivider = rowIdx < rows.length - 1;
                  const notch = notches[row.rowKey];
                  return (
                    // Key by parent menu to avoid re-rendering on leaf item clicks
                    <div
                      key={row.rowKey}
                      ref={(el) => (rowRefs.current[row.rowKey] = el)}
                      className="relative flex flex-nowrap items-center justify-center gap-1 px-4 py-3 overflow-x-auto animate-row-fade"
                      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                    >
                      {row.kids.map((child, idx) => {
                        const commonClass = `flex-shrink-0 px-3 py-1.5 rounded-lg text-[13px] font-medium transition-all duration-200 ease-in-out whitespace-nowrap
                          ${row.selectedLabel === child.label
                            ? "bg-white/20 text-white shadow-sm"
                            : "text-white/70 hover:bg-white/10 hover:text-white"
                          }`;
                        const isLeafRoute = getVisibleChildren(child).length === 0 && child.to && child.to !== "#";

                        // Render standard anchor tags for leaf items so "Open in new tab" works natively
                        if (isLeafRoute) {
                          return (
                            <a
                              key={idx}
                              href={"/#" + child.to}
                              target={child.newTab ? "_blank" : undefined}
                              rel="opener"
                              onClick={(e) => {
                                if (e.ctrlKey || e.metaKey) {
                                  e.preventDefault();
                                  window.open("/#" + child.to, "_blank");
                                  return;
                                }
                                if (child.newTab) return; // let browser handle it, opener stays intact
                                e.preventDefault();
                                clearNav(child.to);
                                closeAll();
                              }}
    
                              className={commonClass}
                            >
                              {child.label}
                            </a>
                          );
                        }

                        return (
                          <button
                            key={idx}
                            ref={(el) => (btnRefs.current[`${row.rowKey}::${child.label}`] = el)}
                            onClick={() => selectAtDepth(row.depth, child)}
                            className={commonClass}
                          >
                            {child.label}
                          </button>
                        );
                      })}

                      {hasDivider &&
                        (notch ? (
                          <svg
                            className="absolute inset-0 pointer-events-none"
                            width="100%"
                            height="100%"
                            viewBox={`0 0 ${notch.width} ${notch.height}`}
                            preserveAspectRatio="none"
                          >
                            <motion.path
                              fill="none"
                              stroke="rgba(255,255,255,0.32)"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              vectorEffect="non-scaling-stroke"
                              initial={false}
                              animate={{ d: notchPath(notch) }}
                              transition={{ duration: 0.35, ease: "easeInOut" }}
                            />
                          </svg>
                        ) : (
                          <div className="absolute left-0 right-0 bottom-0 border-b border-white/20" />
                        ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  );
}